import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cpApi } from '../api/api';
import { useTransactionsFilters } from '../context/TransactionsFilterContext';
import { useDashboardFilter } from '../context/useDashboardFilter';
import VndPageLoading from '../components/VndPageLoading';
import type { DashboardStatsResponse } from '../types';
import { intlLocaleFor } from '../i18n/config';
import { formatVndInt } from '../utils/formatNumber';
import VndDashboardCharts from '../components/VndDashboardCharts';

function formatClosingTime(sec: number, t: TFunction): string {
  if (sec < 60) return t('dashboard.closeSeconds', { count: Math.floor(sec) });
  const min = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  if (min < 60) {
    return s > 0 ? t('dashboard.closeMinSec', { min, sec: s }) : t('dashboard.closeMin', { min });
  }
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? t('dashboard.closeHourMin', { h, m }) : t('dashboard.closeHour', { h });
}

export default function VndDashboardPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const intlLocale = intlLocaleFor(i18n.language);
  const { isResponsible } = useTransactionsFilters() ?? { isResponsible: false };
  const dashboardFilter = useDashboardFilter();
  const [data, setData] = useState<DashboardStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isResponsible) navigate('/requests', { replace: true });
  }, [isResponsible, navigate]);

  const fetchData = useCallback(async (from: string, to: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await cpApi.getDashboardStats({ from, to });
      setData(res);
    } catch {
      setError(t('dashboard.loadError'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const range = dashboardFilter?.range ?? { from: '', to: '' };
  useEffect(() => {
    if (isResponsible && range.from && range.to) void fetchData(range.from, range.to);
    else setLoading(false);
  }, [range.from, range.to, fetchData, isResponsible]);

  const formatUsdt = (n: number) =>
    new Intl.NumberFormat(intlLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  if (!isResponsible) {
    return null;
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-6 w-full">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <VndPageLoading className="py-0" />
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-100 dark:border-gray-700">
              <div className="text-sm text-blue-600 dark:text-blue-400">{t('dashboard.closedIntakes')}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{data.total_payouts}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-100 dark:border-gray-700">
              <div className="text-sm text-teal-600 dark:text-teal-400">{t('dashboard.turnoverVnd')}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">
                {formatVndInt(data.total_vnd ?? 0)}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-100 dark:border-gray-700">
              <div className="text-sm text-amber-600 dark:text-amber-400">{t('dashboard.turnoverUsdt')}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatUsdt(data.total_usdt)}</div>
            </div>
            {(data.total_canceled ?? 0) > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-red-200 dark:border-red-800">
                <div className="text-sm text-red-600 dark:text-red-400">{t('dashboard.canceledIntakes')}</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{data.total_canceled}</div>
              </div>
            )}
            {typeof data.average_closing_sec === 'number' && data.average_closing_sec > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-100 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('dashboard.avgCloseTime')}</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatClosingTime(data.average_closing_sec, t)}
                </div>
              </div>
            )}
          </div>

          <VndDashboardCharts
            sidePanel={
              <>
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-4 border border-slate-200 dark:border-gray-700">
                  <h3 className="text-base font-bold text-slate-800 dark:text-gray-100 mb-3">{t('dashboard.byDirection')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
                      <div className="font-medium text-blue-800 dark:text-blue-200">{t('dashboard.channelQr')}</div>
                      <div className="mt-1.5 text-sm text-gray-600 dark:text-gray-400">
                        {t('dashboard.directionLine', {
                          count: data.by_direction.qr?.count ?? 0,
                          vnd: formatVndInt(data.by_direction.qr?.vnd ?? 0),
                          usdt: formatUsdt(data.by_direction.qr?.usdt ?? 0),
                        })}
                        {(data.by_direction.qr?.canceled_count ?? 0) > 0 && (
                          <span className="text-red-600 dark:text-red-400 ml-1">
                            {' '}
                            · {t('dashboard.canceledShort', { n: data.by_direction.qr.canceled_count })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800">
                      <div className="font-medium text-indigo-800 dark:text-indigo-200">{t('dashboard.channelBank')}</div>
                      <div className="mt-1.5 text-sm text-gray-600 dark:text-gray-400">
                        {t('dashboard.directionLine', {
                          count: data.by_direction.bank?.count ?? 0,
                          vnd: formatVndInt(data.by_direction.bank?.vnd ?? 0),
                          usdt: formatUsdt(data.by_direction.bank?.usdt ?? 0),
                        })}
                        {(data.by_direction.bank?.canceled_count ?? 0) > 0 && (
                          <span className="text-red-600 dark:text-red-400 ml-1">
                            {' '}
                            · {t('dashboard.canceledShort', { n: data.by_direction.bank.canceled_count })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {data.by_account && data.by_account.length > 0 ? (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-4 border border-slate-200 dark:border-gray-700 min-h-0 flex-1 flex flex-col">
                    <h3 className="text-base font-bold text-slate-800 dark:text-gray-100 mb-3">{t('dashboard.byAccount')}</h3>
                    <div className="overflow-auto min-h-0">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-600 text-left text-gray-600 dark:text-gray-400">
                            <th className="pb-2 pr-3 font-semibold">{t('dashboard.colAccount')}</th>
                            <th className="pb-2 pr-3 text-right font-semibold">{t('dashboard.colCount')}</th>
                            <th className="pb-2 pr-3 text-right font-semibold">{t('dashboard.colVnd')}</th>
                            <th className="pb-2 pr-3 text-right font-semibold">{t('dashboard.colMinVnd')}</th>
                            <th className="pb-2 pr-3 text-right font-semibold">{t('dashboard.colMaxVnd')}</th>
                            <th className="pb-2 pr-3 text-right font-semibold">{t('dashboard.colUsdt')}</th>
                            <th className="pb-2 pr-3 text-right font-semibold">{t('dashboard.colMinUsdt')}</th>
                            <th className="pb-2 text-right font-semibold">{t('dashboard.colMaxUsdt')}</th>
                            {data.by_account.some((a) => (a.canceled_count ?? 0) > 0) && (
                              <th className="pb-2 pl-3 text-right font-semibold">{t('dashboard.colCanceled')}</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {data.by_account.map((acc) => (
                            <tr key={acc.counterparty_id} className="border-b border-gray-100 dark:border-gray-700">
                              <td className="py-1.5 pr-3 font-medium text-gray-900 dark:text-white">
                                {acc.name || `#${acc.counterparty_id}`}
                              </td>
                              <td className="py-1.5 pr-3 text-right text-gray-700 dark:text-gray-300">{acc.count}</td>
                              <td className="py-1.5 pr-3 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                                {formatVndInt(acc.vnd ?? 0)}
                              </td>
                              <td className="py-1.5 pr-3 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                                {typeof acc.min_vnd === 'number' ? formatVndInt(acc.min_vnd) : '—'}
                              </td>
                              <td className="py-1.5 pr-3 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                                {typeof acc.max_vnd === 'number' ? formatVndInt(acc.max_vnd) : '—'}
                              </td>
                              <td className="py-1.5 pr-3 text-right text-gray-700 dark:text-gray-300">{formatUsdt(acc.usdt)}</td>
                              <td className="py-1.5 pr-3 text-right text-gray-700 dark:text-gray-300">
                                {typeof acc.min_usdt === 'number' ? formatUsdt(acc.min_usdt) : '—'}
                              </td>
                              <td className="py-1.5 text-right text-gray-700 dark:text-gray-300">
                                {typeof acc.max_usdt === 'number' ? formatUsdt(acc.max_usdt) : '—'}
                              </td>
                              {data.by_account.some((a) => (a.canceled_count ?? 0) > 0) && (
                                <td className="py-1.5 pl-3 text-right text-gray-700 dark:text-gray-300">
                                  {(acc.canceled_count ?? 0) > 0 ? acc.canceled_count : '—'}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </>
            }
          />

          {data.total_payouts === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('dashboard.emptyPeriod')}</div>
          )}
        </>
      )}
    </div>
  );
}
